import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  collection, addDoc, deleteDoc, updateDoc, doc, onSnapshot,
  query, orderBy, serverTimestamp, getCountFromServer, setDoc, getDoc,
} from "firebase/firestore";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import {
  TeamOutlined, FileTextOutlined, BookOutlined, UsergroupAddOutlined,
  ProjectOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, EyeInvisibleOutlined, PlusOutlined, BarChartOutlined, LinkOutlined, HeartOutlined, CloseOutlined,
  CheckOutlined, UploadOutlined, PictureOutlined, VideoCameraOutlined,
  AlignLeftOutlined, LogoutOutlined,
  UpOutlined, DownOutlined, RightOutlined, WifiOutlined, GlobalOutlined, ShoppingOutlined,
} from "@ant-design/icons";
import JoditEditor from "jodit-react";
import "jodit/es2021/jodit.min.css";

const ICON_MAP = {
  community:   TeamOutlined,
  posts:       FileTextOutlined,
  blogs:       BookOutlined,
  subscribers: UsergroupAddOutlined,
  ventures:    ProjectOutlined,
  chart:       BarChartOutlined,
  edit:        EditOutlined,
  trash:       DeleteOutlined,
  eye:         EyeOutlined,
  eyeoff:      EyeInvisibleOutlined,
  plus:        PlusOutlined,
  x:           CloseOutlined,
  check:       CheckOutlined,
  upload:      UploadOutlined,
  image:       PictureOutlined,
  video:       VideoCameraOutlined,
  link:        LinkOutlined,
  poll:        BarChartOutlined,
  text:        AlignLeftOutlined,
  heart:       HeartOutlined,
  logout:      LogoutOutlined,
  chevup:      UpOutlined,
  chevdown:    DownOutlined,
  chevright:   RightOutlined,
  signal:      WifiOutlined,
  enquiries:   GlobalOutlined,
  shop:        ShoppingOutlined,
};

function Ic({ name, size = 16, color = "currentColor" }) {
  const Component = ICON_MAP[name];
  if (!Component) return null;
  return <Component style={{ fontSize: size, color, flexShrink: 0 }} />;
}

const TABS = [
  { id: "dashboard",   label: "Dashboard",    icon: "chart" },
  { id: "shop",        label: "Shop Orders",  icon: "shop" },
  { id: "enquiries",   label: "Enquiries",    icon: "enquiries" },
  { id: "communities", label: "Communities",  icon: "signal" },
  { id: "posts",       label: "Posts",        icon: "posts" },
  { id: "blogs",       label: "Blogs",        icon: "blogs" },
  { id: "subscribers", label: "Subscribers",  icon: "subscribers" },
  { id: "ventures",    label: "Ventures",     icon: "ventures" },
];

const POST_TYPES = [
  { id: "text",  label: "Text",  icon: "text" },
  { id: "image", label: "Image", icon: "image" },
  { id: "video", label: "Video", icon: "video" },
  { id: "poll",  label: "Poll",  icon: "poll" },
  { id: "link",  label: "Link",  icon: "link" },
];

const BLOCK_TYPES = [
  { id: "paragraph", label: "Paragraph", icon: "text" },
  { id: "h2",        label: "Heading 2", icon: null, shortLabel: "H2" },
  { id: "h3",        label: "Heading 3", icon: null, shortLabel: "H3" },
  { id: "code",      label: "Code",      icon: null, shortLabel: "</>" },
  { id: "image",     label: "Image",     icon: "image" },
  { id: "video",     label: "Video",     icon: "video" },
  { id: "quote",     label: "Quote",     icon: null, shortLabel: '"' },
  { id: "list",      label: "List",      icon: null, shortLabel: "•" },
  { id: "divider",   label: "Divider",   icon: null, shortLabel: "—" },
];

const EMPTY_BLOG = { title: "", excerpt: "", thumbnail: "", reelUrl: "", status: "published", blocks: [{ type: "paragraph", content: "" }] };
const slugify = (s) => s.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

export default function Admin() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => { setUser(u); setAuthLoading(false); });
    return () => unsub();
  }, []);
  if (authLoading) return <Loader />;
  if (!user) return <LoginPage />;
  return <Dashboard />;
}

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/user-not-found" || code === "auth/invalid-email") setError("No account found with this email.");
      else if (code === "auth/wrong-password" || code === "auth/invalid-credential") setError("Incorrect password. Please try again.");
      else if (code === "auth/too-many-requests") setError("Too many attempts. Try again later.");
      else if (code === "auth/network-request-failed") setError("Network error. Check your connection.");
      else setError("Sign in failed: " + (err?.message || code || "unknown error"));
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <div style={{ width: "100%", maxWidth: 400, background: "#0E1116", border: "1px solid rgba(199,249,75,0.2)", borderRadius: 24, padding: "44px 40px", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#8aae30,#C7F94B)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ic name="signal" size={18} color="#0A0B0D" />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#EDEFF2", letterSpacing: "0.05em" }}>ADMIN PANEL</span>
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: "#EDEFF2", margin: "0 0 6px", letterSpacing: "-0.02em" }}>
          Welcome back, <span style={{ color: "#C7F94B" }}>Shashank.</span>
        </h1>
        <p style={{ fontSize: 13, color: "rgba(237,239,242,0.4)", marginBottom: 28, lineHeight: 1.5 }}>Sign in to manage your portfolio.</p>
        <form onSubmit={handle} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input className="adm-input" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          <div style={{ position: "relative" }}>
            <input
              className="adm-input"
              type={showPw ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={{ paddingRight: 44 }}
            />
            <button
              type="button"
              onClick={() => setShowPw(v => !v)}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "rgba(237,239,242,0.4)", display: "flex", alignItems: "center" }}
            >
              <Ic name={showPw ? "eyeoff" : "eye"} size={16} color="rgba(237,239,242,0.5)" />
            </button>
          </div>
          {error && <div style={{ fontSize: 12, color: "#f87171", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, padding: "8px 12px" }}>{error}</div>}
          <button className="adm-btn-primary" type="submit" disabled={loading} style={{ marginTop: 4, width: "100%", justifyContent: "center" }}>
            {loading ? "Signing in…" : "Sign In →"}
          </button>
        </form>
      </div>
    </Shell>
  );
}

const ADMIN_CSS = `
  @keyframes adm-spin { to { transform: rotate(360deg); } }
  *, *::before, *::after { box-sizing: border-box; }
  .adm-layout { display:flex; min-height:100vh; background:#0A0B0D; font-family:'Inter','SF Pro Display',-apple-system,sans-serif; color:#EDEFF2; -webkit-font-smoothing:antialiased; }
  /* ── Sidebar ── */
  .adm-sidebar { width:240px; background:#0E1116; border-right:1px solid rgba(255,255,255,0.07); display:flex; flex-direction:column; position:fixed; top:0; left:0; bottom:0; z-index:50; transition:width 0.25s cubic-bezier(0.4,0,0.2,1); overflow:hidden; }
  .adm-layout--collapsed .adm-sidebar { width:64px; }
  .adm-sidebar__top { padding:18px 14px 14px; border-bottom:1px solid rgba(255,255,255,0.06); flex-shrink:0; }
  .adm-sidebar__brand { display:flex; align-items:center; gap:11px; overflow:hidden; }
  .adm-sidebar__logo-icon { width:34px; height:34px; border-radius:10px; background:linear-gradient(135deg,#8aae30,#C7F94B); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .adm-sidebar__brand-name { font-size:14px; font-weight:700; color:#EDEFF2; letter-spacing:-0.01em; white-space:nowrap; transition:opacity 0.2s,width 0.2s; }
  .adm-layout--collapsed .adm-sidebar__brand-name { opacity:0; width:0; overflow:hidden; }
  /* Nav */
  .adm-sidebar__nav { flex:1; padding:10px 8px; display:flex; flex-direction:column; gap:2px; overflow-y:auto; scrollbar-width:none; }
  .adm-nav-item { display:flex; align-items:center; gap:10px; padding:10px 11px; border-radius:10px; background:transparent; border:none; cursor:pointer; font-family:inherit; font-size:13px; font-weight:500; color:rgba(237,239,242,0.5); transition:all 0.17s; white-space:nowrap; width:100%; text-align:left; overflow:hidden; }
  .adm-nav-item:hover { background:rgba(255,255,255,0.05); color:rgba(237,239,242,0.85); }
  .adm-nav-item--active { background:rgba(199,249,75,0.1); color:#C7F94B; }
  .adm-nav-item--active:hover { background:rgba(199,249,75,0.14); }
  .adm-nav-item__icon { flex-shrink:0; display:flex; align-items:center; justify-content:center; width:18px; }
  .adm-nav-item__label { transition:opacity 0.2s; overflow:hidden; flex:1; }
  .adm-layout--collapsed .adm-nav-item__label { opacity:0; width:0; }
  .adm-layout--collapsed .adm-nav-item { justify-content:center; padding:10px; }
  /* Bottom */
  .adm-sidebar__bottom { padding:8px 8px 12px; border-top:1px solid rgba(255,255,255,0.06); display:flex; flex-direction:column; gap:4px; flex-shrink:0; }
  .adm-sidebar__user { display:flex; align-items:center; gap:9px; padding:7px 9px; border-radius:10px; overflow:hidden; }
  .adm-sidebar__avatar { width:32px; height:32px; border-radius:9px; background:linear-gradient(135deg,#8aae30,#C7F94B); display:flex; align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; overflow:hidden; position:relative; transition:opacity 0.15s; }
  .adm-sidebar__avatar:hover { opacity:0.8; }
  .adm-avatar-busy { position:absolute; inset:0; background:rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; }
  .adm-spin-ring { width:14px; height:14px; border-radius:50%; border:2px solid rgba(199,249,75,0.3); border-top-color:#C7F94B; animation:adm-spin 0.7s linear infinite; }
  .adm-sidebar__user-text { display:flex; flex-direction:column; min-width:0; flex:1; transition:opacity 0.2s; overflow:hidden; }
  .adm-layout--collapsed .adm-sidebar__user-text { opacity:0; width:0; }
  .adm-sidebar__user-name { font-size:12.5px; font-weight:600; color:#EDEFF2; white-space:nowrap; }
  .adm-sidebar__user-role { font-size:10px; color:#9AA3AF; }
  .adm-signout-btn { background:none; border:none; cursor:pointer; padding:5px; border-radius:7px; color:rgba(237,239,242,0.35); transition:all 0.17s; flex-shrink:0; display:flex; align-items:center; }
  .adm-signout-btn:hover { color:#f87171; background:rgba(248,113,113,0.08); }
  .adm-layout--collapsed .adm-signout-btn { display:none; }
  .adm-collapse-btn { display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:10px; background:transparent; border:1px solid rgba(255,255,255,0.07); cursor:pointer; font-family:inherit; font-size:12px; font-weight:500; color:rgba(237,239,242,0.4); transition:all 0.17s; width:100%; overflow:hidden; white-space:nowrap; }
  .adm-collapse-btn:hover { background:rgba(255,255,255,0.05); color:rgba(237,239,242,0.7); border-color:rgba(255,255,255,0.12); }
  .adm-collapse-btn__icon { flex-shrink:0; display:flex; align-items:center; width:18px; justify-content:center; transition:transform 0.25s; }
  .adm-layout--collapsed .adm-collapse-btn__icon { transform:rotate(180deg); }
  .adm-collapse-btn__label { transition:opacity 0.2s; overflow:hidden; }
  .adm-layout--collapsed .adm-collapse-btn__label { opacity:0; width:0; }
  .adm-layout--collapsed .adm-collapse-btn { justify-content:center; padding:9px; }
  /* Main */
  .adm-main { flex:1; margin-left:240px; transition:margin-left 0.25s cubic-bezier(0.4,0,0.2,1); display:flex; flex-direction:column; min-height:100vh; }
  .adm-layout--collapsed .adm-main { margin-left:64px; }
  /* Topbar */
  .adm-topbar { position:sticky; top:0; z-index:40; background:rgba(10,11,13,0.92); backdrop-filter:blur(20px); border-bottom:1px solid rgba(255,255,255,0.07); padding:0 28px; height:60px; display:flex; align-items:center; justify-content:space-between; gap:16px; flex-shrink:0; }
  .adm-topbar__left { display:flex; align-items:center; gap:8px; }
  .adm-topbar__section { display:flex; align-items:center; gap:8px; font-size:15px; font-weight:600; color:#EDEFF2; }
  .adm-topbar__stats { display:flex; align-items:center; gap:6px; }
  .adm-stat-chip { display:flex; align-items:center; gap:6px; padding:5px 10px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); border-radius:8px; }
  /* Content */
  .adm-content { flex:1; padding:28px; max-width:1000px; width:100%; }
  /* Shared */
  .adm-input { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); color:#EDEFF2; outline:none; width:100%; border-radius:10px; padding:11px 14px; font-size:13.5px; resize:vertical; box-sizing:border-box; font-family:inherit; transition:border-color .2s,box-shadow .2s; }
  .adm-input::placeholder { color:rgba(237,239,242,0.3); }
  .adm-input:focus { border-color:rgba(199,249,75,0.45); box-shadow:0 0 0 3px rgba(199,249,75,0.07); }
  .adm-btn-primary { background:linear-gradient(135deg,#8aae30,#C7F94B); color:#0A0B0D; font-weight:700; border:none; border-radius:10px; padding:11px 24px; font-size:13.5px; cursor:pointer; font-family:inherit; transition:box-shadow .2s,transform .15s,opacity .15s; display:inline-flex; align-items:center; gap:7px; }
  .adm-btn-primary:hover:not(:disabled) { box-shadow:0 4px 24px rgba(199,249,75,0.3); transform:translateY(-1px); }
  .adm-btn-primary:disabled { opacity:.5; cursor:not-allowed; }
  .adm-row-item { background:rgba(14,17,22,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:12px; padding:14px 16px; display:flex; align-items:center; gap:12px; transition:border-color 0.18s; }
  .adm-row-item:hover { border-color:rgba(255,255,255,0.12); }
  .adm-icon-btn { display:inline-flex; align-items:center; justify-content:center; gap:5px; background:none; border:1px solid rgba(255,255,255,0.1); border-radius:7px; color:rgba(237,239,242,0.45); padding:5px 10px; cursor:pointer; font-family:inherit; font-size:11.5px; font-weight:500; transition:all 0.17s; white-space:nowrap; }
  .adm-icon-btn:hover { color:rgba(237,239,242,0.8); border-color:rgba(255,255,255,0.2); }
  .adm-icon-btn.danger { border-color:rgba(239,68,68,0.2); color:rgba(239,68,68,0.6); }
  .adm-icon-btn.danger:hover { border-color:rgba(239,68,68,0.45); color:rgba(239,68,68,0.9); }
  .adm-icon-btn.accent { border-color:rgba(199,249,75,0.28); color:rgba(199,249,75,0.75); }
  .adm-icon-btn.accent:hover { border-color:rgba(199,249,75,0.5); color:#C7F94B; }
  /* Jodit */
  .jodit-container { border:none !important; background:#0E1116 !important; border-radius:10px !important; overflow:hidden !important; }
  .jodit-toolbar__box { background:#0A0B0D !important; border-bottom:1px solid rgba(255,255,255,0.07) !important; padding:4px 6px !important; }
  .jodit-toolbar-button__button { color:rgba(237,239,242,0.55) !important; border-radius:6px !important; }
  .jodit-toolbar-button__button:hover { background:rgba(199,249,75,0.1) !important; color:#C7F94B !important; }
  .jodit-toolbar-button_activated .jodit-toolbar-button__button { background:rgba(199,249,75,0.12) !important; color:#C7F94B !important; }
  .jodit-toolbar-button__separator { background:rgba(255,255,255,0.08) !important; }
  .jodit-wysiwyg { background:#0E1116 !important; color:#EDEFF2 !important; }
  .jodit-workplace { background:#0E1116 !important; }
  .jodit-placeholder { color:rgba(237,239,242,0.3) !important; }
  .jodit-popup__content { background:#0E1116 !important; border:1px solid rgba(255,255,255,0.1) !important; border-radius:10px !important; }
  .jodit-popup .jodit-input { background:rgba(255,255,255,0.05) !important; border:1px solid rgba(255,255,255,0.1) !important; color:#EDEFF2 !important; border-radius:7px !important; }
  .jodit-ui-button_variant_primary { background:#C7F94B !important; color:#0A0B0D !important; border:none !important; border-radius:7px !important; }
  .jodit-ui-button { background:rgba(255,255,255,0.05) !important; color:rgba(237,239,242,0.6) !important; border:1px solid rgba(255,255,255,0.1) !important; border-radius:7px !important; }
  .jodit-ui-button:hover { background:rgba(255,255,255,0.08) !important; }
  /* Post body */
  .fc-post-body { overflow-wrap:break-word; word-break:break-word; }
  .fc-post-body h2 { font-size:15px; font-weight:700; color:#EDEFF2; margin:10px 0 4px; }
  .fc-post-body h3 { font-size:13.5px; font-weight:600; color:#EDEFF2; margin:8px 0 3px; }
  .fc-post-body p { margin:0 0 8px; }
  .fc-post-body ul,.fc-post-body ol { padding-left:20px; margin:0 0 8px; list-style:disc; }
  .fc-post-body ol { list-style:decimal; }
  .fc-post-body li { margin-bottom:3px; display:list-item; }
  .fc-post-body strong { color:#EDEFF2; font-weight:700; }
  .fc-post-body a { color:#C7F94B; text-decoration:underline; }
  .fc-post-body blockquote { border-left:3px solid rgba(199,249,75,0.4); padding-left:10px; color:rgba(237,239,242,0.55); margin:6px 0; font-style:italic; }
  /* Responsive */
  @media (max-width:900px) { .adm-compose-grid { grid-template-columns:1fr !important; } .adm-topbar__stats { display:none; } }
  /* Modal */
  .adm-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.72); z-index:200; display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(8px); }
  .adm-modal { background:#0E1116; border:1px solid rgba(255,255,255,0.1); border-radius:18px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; animation:adm-modal-in 0.2s ease; scrollbar-width:thin; }
  .adm-modal--wide { max-width:700px; }
  @keyframes adm-modal-in { from { opacity:0; transform:scale(0.96) translateY(12px); } to { opacity:1; transform:scale(1) translateY(0); } }
  .adm-modal__header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid rgba(255,255,255,0.07); position:sticky; top:0; background:#0E1116; z-index:1; }
  .adm-modal__title { font-size:14.5px; font-weight:600; color:#EDEFF2; }
  .adm-modal__body { padding:20px 22px 24px; }
  .adm-modal__close { background:none; border:none; cursor:pointer; padding:6px; border-radius:8px; color:rgba(237,239,242,0.4); display:flex; align-items:center; transition:all 0.15s; }
  .adm-modal__close:hover { color:#EDEFF2; background:rgba(255,255,255,0.07); }
  /* Search & Filters */
  .adm-filter-row { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:14px; }
  .adm-search-wrap { position:relative; flex:1; min-width:180px; }
  .adm-search-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); pointer-events:none; }
  .adm-search-input { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); color:#EDEFF2; outline:none; width:100%; border-radius:10px; padding:9px 14px 9px 36px; font-size:13px; font-family:inherit; transition:border-color .2s,box-shadow .2s; }
  .adm-search-input::placeholder { color:rgba(237,239,242,0.3); }
  .adm-search-input:focus { border-color:rgba(199,249,75,0.45); box-shadow:0 0 0 3px rgba(199,249,75,0.07); }
  .adm-filter-pill { display:flex; align-items:center; gap:5px; padding:6px 12px; border-radius:8px; border:1px solid rgba(255,255,255,0.1); background:transparent; color:rgba(237,239,242,0.5); font-family:inherit; font-size:12px; font-weight:500; cursor:pointer; transition:all 0.17s; white-space:nowrap; }
  .adm-filter-pill:hover { border-color:rgba(255,255,255,0.2); color:rgba(237,239,242,0.8); }
  .adm-filter-pill--active { border-color:rgba(199,249,75,0.45); background:rgba(199,249,75,0.08); color:#C7F94B; }
  .adm-no-results { text-align:center; padding:36px 0; color:rgba(237,239,242,0.28); font-size:13px; }
  /* Section header */
  .adm-section-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; }
  .adm-section-count { font-size:11px; color:rgba(237,239,242,0.35); letter-spacing:0.06em; text-transform:uppercase; }
  @media (max-width:700px) { .adm-ventures-grid { grid-template-columns:1fr !important; } .adm-topbar { padding:0 16px; } .adm-content { padding:16px; } .adm-layout--collapsed .adm-sidebar,.adm-sidebar { width:64px; } .adm-layout--collapsed .adm-main,.adm-main { margin-left:64px; } }
`;
function Dashboard() {
  const [tab, setTab] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [stats, setStats] = useState({ communities: 0, posts: 0, blogs: 0, ventures: 0, enquiries: 0, shopOrders: 0, shopRevenue: 0, shopPending: 0 });
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarInputRef = useRef();

  useEffect(() => {
    getDoc(doc(db, "settings", "profile")).then((snap) => {
      if (snap.exists()) setAvatarUrl(snap.data().avatarUrl || "");
    }).catch(() => {});
  }, []);

  const handleAvatarFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setAvatarBusy(true);
    try {
      const base64 = await resizeToBase64(file, 200, 0.85);
      await setDoc(doc(db, "settings", "profile"), { avatarUrl: base64 }, { merge: true });
      setAvatarUrl(base64);
    } catch (err) {
      console.error("Avatar save failed:", err);
    } finally {
      setAvatarBusy(false);
    }
  };

  useEffect(() => {
    const unsubComs = onSnapshot(collection(db, "communities"), (s) => setStats((p) => ({ ...p, communities: s.size })));
    const unsubPosts = onSnapshot(query(collection(db, "community_posts"), orderBy("createdAt", "desc")), (s) => {
      setStats((p) => ({ ...p, posts: s.docs.filter((d) => (d.data().status || "published") === "published").length }));
    });
    const unsubBlogs = onSnapshot(query(collection(db, "blogs"), orderBy("createdAt", "desc")), (s) => {
      setStats((p) => ({ ...p, blogs: s.docs.filter((d) => (d.data().status || "published") === "published").length }));
    });
    const unsubVen = onSnapshot(collection(db, "venture_submissions"), (s) => setStats((p) => ({ ...p, ventures: s.size })));
    const unsubEnq = onSnapshot(collection(db, "develop_enquiries"), (s) => setStats((p) => ({ ...p, enquiries: s.size })));
    const unsubShop = onSnapshot(collection(db, "shop_orders"), (s) => {
      const paid = s.docs.filter((d) => d.data().status === "paid");
      const revenue = paid.reduce((sum, d) => sum + (d.data().amount || 0), 0);
      setStats((p) => ({ ...p, shopOrders: paid.length, shopRevenue: revenue, shopPending: s.size - paid.length }));
    });
    return () => { unsubComs(); unsubPosts(); unsubBlogs(); unsubVen(); unsubEnq(); unsubShop(); };
  }, []);

  const STAT_CARDS = [
    { label: "Orders",      value: stats.shopOrders,  icon: "shop",     color: "#FFB300" },
    { label: "Revenue",     value: `₹${stats.shopRevenue}`, icon: "shop", color: "#FFB300" },
    { label: "Communities", value: stats.communities, icon: "signal",   color: "#C7F94B" },
    { label: "Posts Live",  value: stats.posts,       icon: "posts",    color: "#818cf8" },
    { label: "Blogs Live",  value: stats.blogs,       icon: "blogs",    color: "#4ade80" },
    { label: "Ventures",    value: stats.ventures,    icon: "ventures", color: "#fb923c" },
    { label: "Enquiries",   value: stats.enquiries,   icon: "enquiries",color: "#38bdf8" },
  ];

  return (
    <div className={`adm-layout${collapsed ? " adm-layout--collapsed" : ""}`}>
      <style>{ADMIN_CSS}</style>

      {/* ── Sidebar ── */}
      <aside className="adm-sidebar">
        <div className="adm-sidebar__top">
          <div className="adm-sidebar__brand">
            <div className="adm-sidebar__logo-icon">
              <Ic name="signal" size={16} color="#0A0B0D" />
            </div>
            <span className="adm-sidebar__brand-name">Admin</span>
          </div>
        </div>

        <nav className="adm-sidebar__nav">
          {TABS.map((t, i) => (
            <React.Fragment key={t.id}>
              {i === 2 && <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "6px 4px" }} />}
              <button
                onClick={() => setTab(i)}
                className={`adm-nav-item${tab === i ? " adm-nav-item--active" : ""}`}
                title={t.label}
              >
                <span className="adm-nav-item__icon"><Ic name={t.icon} size={16} /></span>
                <span className="adm-nav-item__label">{t.label}</span>
              </button>
            </React.Fragment>
          ))}
        </nav>

        <div className="adm-sidebar__bottom">
          <div className="adm-sidebar__user">
            <div
              className="adm-sidebar__avatar"
              onClick={() => !avatarBusy && avatarInputRef.current?.click()}
              title="Click to update photo"
            >
              {avatarUrl
                ? <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ fontSize: 13, fontWeight: 700, color: "#0A0B0D" }}>S</span>}
              {avatarBusy && <div className="adm-avatar-busy"><div className="adm-spin-ring" /></div>}
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => handleAvatarFile(e.target.files?.[0])}
            />
            <div className="adm-sidebar__user-text">
              <span className="adm-sidebar__user-name">Shashank</span>
              <span className="adm-sidebar__user-role">Administrator</span>
            </div>
            <button onClick={() => signOut(auth)} className="adm-signout-btn" title="Sign Out">
              <Ic name="logout" size={14} />
            </button>
          </div>
          <button className="adm-collapse-btn" onClick={() => setCollapsed(v => !v)}>
            <span className="adm-collapse-btn__icon"><Ic name="chevright" size={13} /></span>
            <span className="adm-collapse-btn__label">Collapse</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="adm-main">
        <div className="adm-topbar">
          <div className="adm-topbar__left">
            <span className="adm-topbar__section">
              <Ic name={TABS[tab].icon} size={15} color="#C7F94B" />
              {TABS[tab].label}
            </span>
          </div>
          <div className="adm-topbar__stats">
            {STAT_CARDS.map(s => (
              <div key={s.label} className="adm-stat-chip">
                <Ic name={s.icon} size={12} color={s.color} />
                <span style={{ color: s.color, fontWeight: 700, fontSize: 13 }}>{s.value}</span>
                <span style={{ fontSize: 11, color: "#9AA3AF" }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="adm-content">
          {tab === 0 && <DashboardTab stats={stats} />}
          {tab === 1 && <ShopOrdersTab stats={stats} />}
          {tab === 2 && <EnquiriesTab />}
          {tab === 3 && <CommunitiesTab />}
          {tab === 4 && <PostsTab />}
          {tab === 5 && <BlogsTab />}
          {tab === 6 && <SubscribersTab />}
          {tab === 7 && <VenturesTab />}
        </div>
      </main>
    </div>
  );
}

function StatusToggle({ value, onChange }) {
  return (
    <div style={{ display: "inline-flex", gap: 2, background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 3, border: "1px solid rgba(255,255,255,0.07)" }}>
      <button onClick={() => onChange("published")} style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: "inherit", transition: "all 0.17s", background: value === "published" ? "rgba(74,222,128,0.12)" : "transparent", color: value === "published" ? "#4ade80" : "rgba(237,239,242,0.35)" }}>
        <Ic name="eye" size={12} color={value === "published" ? "#4ade80" : "rgba(237,239,242,0.35)"} /> Published
      </button>
      <button onClick={() => onChange("draft")} style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: "inherit", transition: "all 0.17s", background: value === "draft" ? "rgba(255,255,255,0.07)" : "transparent", color: value === "draft" ? "rgba(237,239,242,0.65)" : "rgba(237,239,242,0.35)" }}>
        <Ic name="eyeoff" size={12} color={value === "draft" ? "rgba(237,239,242,0.65)" : "rgba(237,239,242,0.35)"} /> Draft
      </button>
    </div>
  );
}

function StatusBadge({ status }) {
  const pub = (status || "published") === "published";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, padding: "2px 8px", borderRadius: 6, fontWeight: 600, border: `1px solid ${pub ? "rgba(74,222,128,0.25)" : "rgba(255,255,255,0.1)"}`, background: pub ? "rgba(74,222,128,0.08)" : "rgba(255,255,255,0.05)", color: pub ? "#4ade80" : "rgba(237,239,242,0.4)" }}>
      <Ic name={pub ? "eye" : "eyeoff"} size={10} color={pub ? "#4ade80" : "rgba(237,239,242,0.4)"} />
      {pub ? "Published" : "Draft"}
    </span>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="adm-search-wrap">
      <svg className="adm-search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="rgba(237,239,242,0.3)" strokeWidth="1.3"/>
        <path d="M9.5 9.5L12 12" stroke="rgba(237,239,242,0.3)" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
      <input className="adm-search-input" placeholder={placeholder || "Search…"} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Modal({ open, onClose, title, wide, children }) {
  if (!open) return null;
  return (
    <div className="adm-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`adm-modal${wide ? " adm-modal--wide" : ""}`}>
        <div className="adm-modal__header">
          <span className="adm-modal__title">{title}</span>
          <button className="adm-modal__close" onClick={onClose}><Ic name="x" size={15} /></button>
        </div>
        <div className="adm-modal__body">{children}</div>
      </div>
    </div>
  );
}

function ShopOrdersTab({ stats }) {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mailState, setMailState] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, email }

  useEffect(() => {
    const q = query(collection(db, "shop_orders"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const sendMail = async (o) => {
    setMailState((p) => ({ ...p, [o.id]: "sending" }));
    try {
      const res = await fetch("/.netlify/functions/send-order-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: o.name, email: o.email }),
      });
      const data = await res.json();
      if (data.success) {
        setMailState((p) => ({ ...p, [o.id]: "sent" }));
        setTimeout(() => setMailState((p) => ({ ...p, [o.id]: "idle" })), 4000);
      } else {
        throw new Error(data.error || "Failed");
      }
    } catch (err) {
      console.error("Send mail error:", err);
      setMailState((p) => ({ ...p, [o.id]: "error" }));
      setTimeout(() => setMailState((p) => ({ ...p, [o.id]: "idle" })), 4000);
    }
  };

  const deleteOrder = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteDoc(doc(db, "shop_orders", deleteConfirm.id));
    } catch (err) {
      console.error("Delete order error:", err);
    }
    setDeleteConfirm(null);
  };

  const statusBadge = (status) => {
    if (status === "paid") return { bg: "rgba(74,222,128,0.08)", border: "rgba(74,222,128,0.25)", color: "#4ade80" };
    return { bg: "rgba(255,179,0,0.08)", border: "rgba(255,179,0,0.25)", color: "#FFB300" };
  };

  const filtered = orders
    .filter((o) => statusFilter === "all" || o.status === statusFilter)
    .filter((o) => !search || [o.name, o.email, o.paymentId, o.orderId].some((f) => (f || "").toLowerCase().includes(search.toLowerCase())));

  const paidCount = orders.filter((o) => o.status === "paid").length;
  const pendingCount = orders.filter((o) => o.status === "pending").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        {[
          { label: "Paid Orders",   value: stats.shopOrders,        color: "#FFB300" },
          { label: "Total Revenue", value: `₹${stats.shopRevenue}`, color: "#4ade80" },
          { label: "Avg Order",     value: stats.shopOrders ? `₹${Math.round(stats.shopRevenue / stats.shopOrders)}` : "—", color: "#818cf8" },
          { label: "Pending",       value: stats.shopPending || 0,  color: "#fb923c" },
        ].map((c) => (
          <div key={c.label} style={{ background: "#0E1116", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "18px 20px" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color, marginBottom: 4 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: "rgba(237,239,242,0.4)" }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filters row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {[
          { key: "all",     label: `All (${orders.length})` },
          { key: "paid",    label: `Paid (${paidCount})` },
          { key: "pending", label: `Pending (${pendingCount})` },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            style={{
              padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: statusFilter === f.key ? "1px solid #FFB300" : "1px solid rgba(255,255,255,0.1)",
              background: statusFilter === f.key ? "rgba(255,179,0,0.1)" : "transparent",
              color: statusFilter === f.key ? "#FFB300" : "rgba(237,239,242,0.5)",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Search + count */}
      <div className="adm-section-header">
        <span className="adm-section-count">
          {filtered.length} order{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search by name, email or payment ID…" />

      {/* Order rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {orders.length === 0 && <EmptyState icon="shop" text="No orders yet. Share your shop link to start selling." />}
        {orders.length > 0 && filtered.length === 0 && <div className="adm-no-results">No orders match your filters.</div>}
        {filtered.map((o) => {
          const ms = mailState[o.id] || "idle";
          const badge = statusBadge(o.status);
          return (
            <div key={o.id} className="adm-row-item">
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,179,0,0.1)", border: "1px solid rgba(255,179,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Ic name="shop" size={18} color="#FFB300" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#EDEFF2", marginBottom: 2 }}>{o.name || "—"}</div>
                <div style={{ fontSize: 12, color: "rgba(237,239,242,0.5)", marginBottom: 3 }}>{o.email}</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {o.paymentId && <span style={{ fontSize: 11, color: "rgba(237,239,242,0.3)", fontFamily: "monospace" }}>{o.paymentId}</span>}
                  <span style={{ fontSize: 11, color: "rgba(237,239,242,0.25)" }}>{o.createdAt?.toDate?.()?.toLocaleString() || "—"}</span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#FFB300" }}>₹{o.amount}</div>
                  <span style={{ display: "inline-block", marginTop: 4, fontSize: 10.5, padding: "2px 8px", borderRadius: 6, fontWeight: 600, background: badge.bg, border: `1px solid ${badge.border}`, color: badge.color }}>
                    {o.status || "paid"}
                  </span>
                </div>
                {o.status === "paid" && (
                  <button
                    className="adm-icon-btn"
                    onClick={() => sendMail(o)}
                    disabled={ms === "sending"}
                    style={{
                      borderColor: ms === "sent" ? "rgba(74,222,128,0.4)" : ms === "error" ? "rgba(239,68,68,0.4)" : undefined,
                      color: ms === "sent" ? "#4ade80" : ms === "error" ? "#f87171" : undefined,
                      minWidth: 100,
                      justifyContent: "center",
                    }}
                  >
                    <Ic name={ms === "sent" ? "check" : ms === "error" ? "x" : "enquiries"} size={12} color={ms === "sent" ? "#4ade80" : ms === "error" ? "#f87171" : undefined} />
                    {ms === "sending" ? "Sending…" : ms === "sent" ? "Mail Sent!" : ms === "error" ? "Failed" : "Send Mail"}
                  </button>
                )}
                <button
                  className="adm-icon-btn"
                  onClick={() => setDeleteConfirm({ id: o.id, name: o.name, email: o.email })}
                  style={{
                    borderColor: "rgba(239,68,68,0.2)",
                    color: "rgba(239,68,68,0.5)",
                    minWidth: 100,
                    justifyContent: "center",
                  }}
                >
                  <Ic name="x" size={12} color="rgba(239,68,68,0.5)" />
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div
          onClick={() => setDeleteConfirm(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#13131f", border: "1px solid rgba(239,68,68,0.25)",
              borderRadius: 16, padding: "28px 28px 24px", width: 340, boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 12 }}>🗑️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#EDEFF2", marginBottom: 8 }}>Delete order?</div>
            <div style={{ fontSize: 13, color: "rgba(237,239,242,0.5)", marginBottom: 24, lineHeight: 1.5 }}>
              This will permanently remove the order from <strong style={{ color: "rgba(237,239,242,0.75)" }}>{deleteConfirm.name}</strong> ({deleteConfirm.email}).
              <br />This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)",
                  background: "transparent", color: "rgba(237,239,242,0.6)", cursor: "pointer", fontSize: 14, fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={deleteOrder}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg,#dc2626,#b91c1c)",
                  color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 700,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommunitiesTab() {
  const [communities, setCommunities] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", slug: "", emoji: "", imageUrl: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    const q = query(collection(db, "communities"), orderBy("createdAt", "desc"));
    return onSnapshot(q, async (snap) => {
      setLoadError("");
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCommunities(list);
      const c = {};
      await Promise.all(list.map(async (comm) => {
        try {
          const s = await getCountFromServer(collection(db, "communities", comm.id, "subscribers"));
          c[comm.id] = s.data().count;
        } catch { c[comm.id] = 0; }
      }));
      setCounts(c);
    }, (err) => {
      console.error("Communities load error:", err);
      setLoadError(`Failed to load communities: ${err.code || err.message}`);
    });
  }, []);

  const upd = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const openAdd = () => {
    setEditingId(null);
    setForm({ name: "", description: "", slug: "", emoji: "", imageUrl: "" });
    setSaveError("");
    setModal(true);
  };

  const openEdit = (c) => {
    setForm({ name: c.name, description: c.description || "", slug: c.slug, emoji: c.emoji || "", imageUrl: c.imageUrl || "" });
    setEditingId(c.id);
    setSaveError("");
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setEditingId(null);
    setForm({ name: "", description: "", slug: "", emoji: "", imageUrl: "" });
    setSaveError("");
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError("");
    const data = { ...form, slug: slugify(form.slug || form.name) };
    try {
      if (editingId) { await updateDoc(doc(db, "communities", editingId), data); }
      else { await addDoc(collection(db, "communities"), { ...data, createdAt: serverTimestamp() }); }
      closeModal();
    } catch (err) {
      console.error("Save community error:", err);
      setSaveError(`Save failed: ${err.code || err.message}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this community? This cannot be undone.")) return;
    await deleteDoc(doc(db, "communities", id));
  };

  const filtered = search
    ? communities.filter(c => [c.name, c.description, c.slug].some(f => (f || "").toLowerCase().includes(search.toLowerCase())))
    : communities;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="adm-section-header">
        <span className="adm-section-count">
          {search ? `${filtered.length} of ${communities.length}` : `${communities.length}`} communit{communities.length !== 1 ? "ies" : "y"}
        </span>
        <button className="adm-btn-primary" onClick={openAdd} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, padding: "8px 18px" }}>
          <Ic name="plus" size={14} color="#0A0B0D" /> Add Community
        </button>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search by name, description or slug…" />

      {loadError && <p style={{ margin: 0, fontSize: 12, color: "#f87171" }}>{loadError}</p>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {communities.length === 0 && !loadError && <EmptyState icon="signal" text="No communities yet. Add one to get started." />}
        {communities.length > 0 && filtered.length === 0 && <div className="adm-no-results">No communities match "{search}"</div>}
        {filtered.map((c) => (
          <div key={c.id} className="adm-row-item">
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg,#8aae30,#C7F94B)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, overflow: "hidden" }}>
              {c.imageUrl ? <img src={c.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} /> : (c.emoji || <Ic name="signal" size={20} color="rgba(255,255,255,0.8)" />)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: "#EDEFF2", marginBottom: 3 }}>{c.name}</div>
              {c.description && <div style={{ fontSize: 12, color: "rgba(237,239,242,0.45)", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description}</div>}
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "rgba(199,249,75,0.65)", fontFamily: "monospace" }}>/community/{c.slug}</span>
                <span style={{ fontSize: 11, color: "rgba(237,239,242,0.3)" }}>· {counts[c.id] ?? "…"} members</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="adm-icon-btn accent" onClick={() => openEdit(c)}><Ic name="edit" size={13} /> Edit</button>
              <button className="adm-icon-btn danger" onClick={() => handleDelete(c.id)}><Ic name="trash" size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={modal} onClose={closeModal} title={editingId ? "Edit Community" : "New Community"}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ width: 60, height: 60, borderRadius: 14, flexShrink: 0, background: "linear-gradient(135deg,#8aae30,#C7F94B)", border: "1px solid rgba(199,249,75,0.25)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontSize: 26 }}>
            {form.imageUrl
              ? <img src={form.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
              : (form.emoji || <Ic name="signal" size={24} color="rgba(255,255,255,0.6)" />)}
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <MediaUpload value={form.imageUrl} onChange={(url) => upd("imageUrl", url)} label="Upload Image" />
            <div style={{ display: "flex", gap: 8 }}>
              <input className="adm-input" placeholder="Emoji" value={form.emoji} onChange={(e) => upd("emoji", e.target.value)} style={{ maxWidth: 90, fontSize: 18 }} />
              <input className="adm-input" placeholder="Community name *" value={form.name} onChange={(e) => { upd("name", e.target.value); if (!editingId) upd("slug", slugify(e.target.value)); }} />
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input className="adm-input" placeholder="Description" value={form.description} onChange={(e) => upd("description", e.target.value)} />
          <div style={{ display: "flex", alignItems: "center", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, overflow: "hidden" }}>
            <span style={{ padding: "11px 12px", fontSize: 11, color: "rgba(237,239,242,0.3)", borderRight: "1px solid rgba(255,255,255,0.06)", flexShrink: 0, whiteSpace: "nowrap" }}>…/community/</span>
            <input style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "11px 14px", fontSize: 13, fontWeight: 600, color: "#C7F94B", fontFamily: "inherit" }} placeholder="your-slug" value={form.slug} onChange={(e) => upd("slug", slugify(e.target.value))} />
          </div>
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <button className="adm-btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? "Saving…" : editingId ? "Update Community" : "Create Community"}
          </button>
          <button className="adm-icon-btn" onClick={closeModal}>Cancel</button>
        </div>
        {saveError && <p style={{ margin: "8px 0 0", fontSize: 12, color: "#f87171" }}>{saveError}</p>}
      </Modal>
    </div>
  );
}
function PostsTab() {
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [modal, setModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postType, setPostType] = useState("text");
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [status, setStatus] = useState("published");
  const [posting, setPosting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    const q = query(collection(db, "communities"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCommunities(list);
      if (!selectedCommunity && list.length > 0) setSelectedCommunity(list[0].id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = query(collection(db, "community_posts"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPosts(selectedCommunity ? all.filter((p) => p.communityId === selectedCommunity) : all);
    });
  }, [selectedCommunity]);

  useEffect(() => {
    if (!editingPost) return;
    setPostType(editingPost.type || "text"); setText(editingPost.text || "");
    setImageUrl(editingPost.imageUrl || ""); setVideoUrl(editingPost.videoUrl || "");
    setLinkUrl(editingPost.linkUrl || ""); setLinkTitle(editingPost.linkTitle || "");
    setPollQuestion(editingPost.pollQuestion || "");
    setPollOptions(editingPost.pollOptions?.map((o) => o.text) || ["", ""]);
    setStatus(editingPost.status || "published");
  }, [editingPost]);

  const resetForm = () => {
    setEditingPost(null); setPostType("text"); setText(""); setImageUrl(""); setVideoUrl("");
    setLinkUrl(""); setLinkTitle(""); setPollQuestion(""); setPollOptions(["", ""]); setStatus("published");
  };

  const openNew = () => { resetForm(); setModal(true); };
  const openEdit = (p) => { setEditingPost(p); setModal(true); };
  const closeModal = () => { setModal(false); resetForm(); };

  const handlePost = async () => {
    if (!selectedCommunity) return;
    const plainText = text.replace(/<[^>]*>/g, "").trim();
    if (postType === "text" && !plainText) return;
    if (postType === "poll" && (!pollQuestion.trim() || pollOptions.filter((o) => o.trim()).length < 2)) return;
    setPosting(true);
    try {
      const base = { type: postType, text: plainText ? text : null, status, communityId: selectedCommunity };
      if (postType === "image") Object.assign(base, { imageUrl: imageUrl.trim() || null });
      if (postType === "video") Object.assign(base, { videoUrl: videoUrl.trim() || null });
      if (postType === "link")  Object.assign(base, { linkUrl: linkUrl.trim() || null, linkTitle: linkTitle.trim() || null });
      if (postType === "poll")  Object.assign(base, { pollQuestion: pollQuestion.trim(), pollOptions: pollOptions.filter((o) => o.trim()).map((o) => ({ text: o.trim(), votes: 0 })) });
      if (editingPost) { await updateDoc(doc(db, "community_posts", editingPost.id), { ...base, updatedAt: serverTimestamp() }); }
      else { await addDoc(collection(db, "community_posts"), { ...base, likeCount: 0, createdAt: serverTimestamp() }); }
      closeModal();
    } finally { setPosting(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm("Delete post?")) return; await deleteDoc(doc(db, "community_posts", id)); };
  const toggleStatus = async (p) => { await updateDoc(doc(db, "community_posts", p.id), { status: (p.status || "published") === "published" ? "draft" : "published" }); };
  const addPollOption = () => { if (pollOptions.length < 10) setPollOptions((p) => [...p, ""]); };
  const removePollOption = (i) => { if (pollOptions.length > 2) setPollOptions((p) => p.filter((_, idx) => idx !== i)); };
  const updatePollOption = (i, val) => setPollOptions((p) => p.map((o, idx) => idx === i ? val : o));

  const editorRef = useRef(null);
  const joditConfig = useMemo(() => ({
    theme: "default", toolbarButtonSize: "small", minHeight: 200,
    placeholder: postType === "image" || postType === "video" ? "Caption (optional)…" : "Write your message…",
    style: { background: "#0E1116", color: "#EDEFF2", fontFamily: "'Inter', -apple-system, sans-serif", fontSize: "14.5px", lineHeight: "1.75", padding: "14px 16px" },
    toolbarAdaptive: false, statusbar: false, showXPathInStatusbar: false,
    showCharsCounter: false, showWordsCounter: false,
    uploader: { insertImageAsBase64URI: true }, limitChars: 50000,
    askBeforePasteFromWord: false, askBeforePasteHTML: false, defaultActionOnPaste: "insert_clear_html",
    buttons: ["bold","italic","underline","strikethrough","|","ul","ol","|","font","fontsize","brush","paragraph","|","image","table","|","link","|","left","center","right","|","undo","redo","|","eraser","source"],
    extraCSS: `body{background:#0E1116!important;color:#EDEFF2!important;font-family:'Inter',-apple-system,sans-serif!important;font-size:14.5px!important;line-height:1.75!important;}p{margin:0 0 10px;}h2{font-size:18px;font-weight:700;color:#EDEFF2;margin:14px 0 6px;}ul,ol{padding-left:20px;margin:0 0 10px;}strong{color:#EDEFF2;}a{color:#C7F94B;}blockquote{border-left:3px solid rgba(199,249,75,0.5);padding-left:12px;color:rgba(237,239,242,0.55);margin:8px 0;font-style:italic;}`,
  }), [postType]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentCommunity = communities.find((c) => c.id === selectedCommunity);
  const filteredPosts = posts.filter(p => {
    if (typeFilter !== "all" && (p.type || "text") !== typeFilter) return false;
    if (statusFilter !== "all" && (p.status || "published") !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const body = ((p.text || "").replace(/<[^>]*>/g, " ") + " " + (p.pollQuestion || "")).toLowerCase();
      return body.includes(q);
    }
    return true;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Community tabs + New Post button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {communities.map((c) => (
            <button key={c.id} onClick={() => { setSelectedCommunity(c.id); setSearch(""); setStatusFilter("all"); setTypeFilter("all"); }}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 500, transition: "all 0.17s", border: `1px solid ${selectedCommunity === c.id ? "rgba(199,249,75,0.45)" : "rgba(255,255,255,0.1)"}`, background: selectedCommunity === c.id ? "rgba(199,249,75,0.08)" : "transparent", color: selectedCommunity === c.id ? "#C7F94B" : "rgba(237,239,242,0.5)" }}>
              <Ic name="signal" size={13} color={selectedCommunity === c.id ? "#C7F94B" : "rgba(237,239,242,0.3)"} />
              {c.name}
            </button>
          ))}
          {communities.length === 0 && <span style={{ fontSize: 12, color: "rgba(237,239,242,0.35)" }}>No communities — create one in the Communities tab first.</span>}
        </div>
        <button className="adm-btn-primary" onClick={openNew} disabled={!selectedCommunity} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, padding: "8px 18px", flexShrink: 0 }}>
          <Ic name="plus" size={14} color="#0A0B0D" /> New Post
        </button>
      </div>

      {/* Filter row */}
      <div className="adm-filter-row">
        <SearchBar value={search} onChange={v => { setSearch(v); }} placeholder="Search post content…" />
        <div style={{ display:"flex", gap:5 }}>
          {["all","published","draft"].map(s => (
            <button key={s} className={`adm-filter-pill${statusFilter === s ? " adm-filter-pill--active" : ""}`} onClick={() => setStatusFilter(s)}>
              {s === "all" ? "All status" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          <button className={`adm-filter-pill${typeFilter === "all" ? " adm-filter-pill--active" : ""}`} onClick={() => setTypeFilter("all")}>All types</button>
          {POST_TYPES.map(t => (
            <button key={t.id} className={`adm-filter-pill${typeFilter === t.id ? " adm-filter-pill--active" : ""}`} onClick={() => setTypeFilter(t.id)}>
              <Ic name={t.icon} size={12} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Post list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <div className="adm-section-count">
          {search || statusFilter !== "all" || typeFilter !== "all" ? `${filteredPosts.length} of ${posts.length}` : `${posts.length}`} post{posts.length !== 1 ? "s" : ""}{currentCommunity ? ` · ${currentCommunity.name}` : ""}
        </div>
        {posts.length === 0 && <EmptyState icon="posts" text="No posts in this community yet." />}
        {posts.length > 0 && filteredPosts.length === 0 && <div className="adm-no-results">No posts match your filters</div>}
        {filteredPosts.map((p) => {
          const pt = POST_TYPES.find((t) => t.id === (p.type || "text"));
          return (
            <div key={p.id} className="adm-row-item">
              <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(199,249,75,0.08)", border: "1px solid rgba(199,249,75,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Ic name={pt?.icon || "text"} size={15} color="#C7F94B" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10.5, padding: "1px 7px", borderRadius: 5, background: "rgba(199,249,75,0.08)", color: "rgba(199,249,75,0.85)", border: "1px solid rgba(199,249,75,0.2)", textTransform: "uppercase", fontWeight: 600 }}>{p.type || "text"}</span>
                  <StatusBadge status={p.status} />
                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(237,239,242,0.3)" }}>
                    <Ic name="heart" size={11} color="rgba(237,239,242,0.3)" /> {p.likeCount || 0}
                  </span>
                </div>
                <p style={{ margin: "0 0 3px", fontSize: 12.5, color: "rgba(237,239,242,0.65)", lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {p.type === "poll" ? p.pollQuestion : (p.text || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()}
                </p>
                <span style={{ fontSize: 10.5, color: "rgba(237,239,242,0.25)" }}>{p.createdAt?.toDate?.()?.toLocaleString() || "Just now"}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                <button className="adm-icon-btn accent" onClick={() => openEdit(p)}><Ic name="edit" size={12} /> Edit</button>
                <button className="adm-icon-btn" onClick={() => toggleStatus(p)} style={{ fontSize: 11 }}>
                  <Ic name={(p.status || "published") === "published" ? "eyeoff" : "eye"} size={12} />
                  {(p.status || "published") === "published" ? "Draft" : "Publish"}
                </button>
                <button className="adm-icon-btn danger" onClick={() => handleDelete(p.id)}><Ic name="trash" size={12} /> Delete</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compose modal */}
      <Modal open={modal} onClose={closeModal} title={editingPost ? "Edit Post" : `New Post${currentCommunity ? ` · ${currentCommunity.name}` : ""}`} wide>
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          {POST_TYPES.map((t) => (
            <button key={t.id} onClick={() => setPostType(t.id)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 13px", borderRadius: 8, fontSize: 12.5, fontWeight: 500, border: `1px solid ${postType === t.id ? "rgba(199,249,75,0.5)" : "rgba(255,255,255,0.1)"}`, background: postType === t.id ? "rgba(199,249,75,0.09)" : "transparent", color: postType === t.id ? "#C7F94B" : "rgba(237,239,242,0.45)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.17s" }}>
              <Ic name={t.icon} size={13} color={postType === t.id ? "#C7F94B" : "rgba(237,239,242,0.35)"} />
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {postType === "poll" && <input className="adm-input" placeholder="Poll question…" value={pollQuestion} onChange={(e) => setPollQuestion(e.target.value)} />}
          <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)" }}>
            <JoditEditor ref={editorRef} value={text} config={joditConfig} onBlur={(val) => setText(val)} />
          </div>
          {postType === "image" && <MediaUpload value={imageUrl} onChange={setImageUrl} label="Upload Image" maxPx={700} />}
          {postType === "video" && <MediaUpload value={videoUrl} onChange={setVideoUrl} accept="video/*" label="Upload Video" />}
          {postType === "link" && (
            <div style={{ display: "flex", gap: 10 }}>
              <input className="adm-input" placeholder="Link URL" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
              <input className="adm-input" placeholder="Link title" value={linkTitle} onChange={(e) => setLinkTitle(e.target.value)} />
            </div>
          )}
          {postType === "poll" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {pollOptions.map((opt, i) => (
                <div key={i} style={{ display: "flex", gap: 8 }}>
                  <input className="adm-input" placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => updatePollOption(i, e.target.value)} />
                  {pollOptions.length > 2 && (
                    <button onClick={() => removePollOption(i)} className="adm-icon-btn danger" style={{ flexShrink: 0, padding: "8px" }}><Ic name="x" size={13} /></button>
                  )}
                </div>
              ))}
              {pollOptions.length < 10 && (
                <button onClick={addPollOption} style={{ background: "none", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 8, color: "rgba(237,239,242,0.4)", padding: "8px", cursor: "pointer", fontFamily: "inherit", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <Ic name="plus" size={13} color="rgba(237,239,242,0.4)" /> Add option
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <button className="adm-btn-primary" onClick={handlePost} disabled={posting || !selectedCommunity} style={{ fontSize: 13, padding: "9px 22px" }}>
            {posting ? "Saving…" : editingPost ? "Update Post" : "Post"}
          </button>
          <StatusToggle value={status} onChange={setStatus} />
          <button className="adm-icon-btn" onClick={closeModal} style={{ marginLeft: "auto" }}>Cancel</button>
        </div>
      </Modal>
    </div>
  );
}
function BlogsTab() {
  const [blogs, setBlogs] = useState([]);
  const [mode, setMode] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_BLOG);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => setBlogs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const handleNew = () => { setForm({ ...EMPTY_BLOG, blocks: [{ type: "paragraph", content: "" }] }); setEditingId(null); setMode("new"); };
  const handleEdit = (b) => { setForm({ title: b.title || "", excerpt: b.excerpt || "", thumbnail: b.thumbnail || "", reelUrl: b.reelUrl || "", status: b.status || "published", blocks: b.blocks?.length ? b.blocks : [{ type: "paragraph", content: "" }] }); setEditingId(b.id); setMode("edit"); };
  const handleDelete = async (id) => { if (!window.confirm("Delete blog?")) return; await deleteDoc(doc(db, "blogs", id)); };
  const toggleStatus = async (b) => { await updateDoc(doc(db, "blogs", b.id), { status: (b.status || "published") === "published" ? "draft" : "published" }); };
  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingId) { await updateDoc(doc(db, "blogs", editingId), { ...form, updatedAt: serverTimestamp() }); }
      else { await addDoc(collection(db, "blogs"), { ...form, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }); }
      setMode("list");
    } finally { setSaving(false); }
  };

  if (mode !== "list") return <BlogEditor form={form} setForm={setForm} onSave={handleSave} onCancel={() => setMode("list")} saving={saving} isEdit={mode === "edit"} />;

  const filteredBlogs = blogs.filter(b => {
    if (statusFilter !== "all" && (b.status || "published") !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (b.title || "").toLowerCase().includes(q) || (b.excerpt || "").toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div>
      <div className="adm-section-header" style={{ marginBottom: 12 }}>
        <span className="adm-section-count">
          {search || statusFilter !== "all" ? `${filteredBlogs.length} of ${blogs.length}` : `${blogs.length}`} blog{blogs.length !== 1 ? "s" : ""}
        </span>
        <button className="adm-btn-primary" onClick={handleNew} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, padding: "8px 18px" }}>
          <Ic name="plus" size={14} color="#0A0B0D" /> New Blog
        </button>
      </div>
      <div className="adm-filter-row">
        <SearchBar value={search} onChange={setSearch} placeholder="Search by title or excerpt…" />
        <div style={{ display:"flex", gap:5 }}>
          {["all","published","draft"].map(s => (
            <button key={s} className={`adm-filter-pill${statusFilter === s ? " adm-filter-pill--active" : ""}`} onClick={() => setStatusFilter(s)}>
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {blogs.length === 0 && <EmptyState icon="blogs" text="No blogs yet. Write your first one." />}
        {blogs.length > 0 && filteredBlogs.length === 0 && <div className="adm-no-results">No blogs match your filters</div>}
        {filteredBlogs.map((b) => (
          <div key={b.id} className="adm-row-item">
            {b.thumbnail
              ? <img src={b.thumbnail} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} onError={(e) => { e.target.style.display = "none"; }} />
              : <div style={{ width: 48, height: 48, borderRadius: 10, background: "rgba(199,249,75,0.08)", border: "1px solid rgba(199,249,75,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic name="blogs" size={18} color="rgba(199,249,75,0.6)" /></div>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#EDEFF2", marginBottom: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <StatusBadge status={b.status} />
                <span style={{ fontSize: 11, color: "rgba(237,239,242,0.3)" }}>{b.blocks?.length || 0} blocks · {b.createdAt?.toDate?.()?.toLocaleDateString() || "—"}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button className="adm-icon-btn accent" onClick={() => handleEdit(b)}><Ic name="edit" size={13} /> Edit</button>
              <button className="adm-icon-btn" onClick={() => toggleStatus(b)} style={{ fontSize: 11 }}>
                <Ic name={(b.status || "published") === "published" ? "eyeoff" : "eye"} size={12} />
                {(b.status || "published") === "published" ? "Draft" : "Publish"}
              </button>
              <button className="adm-icon-btn danger" onClick={() => handleDelete(b.id)}><Ic name="trash" size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogEditor({ form, setForm, onSave, onCancel, saving, isEdit }) {
  const upd = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const updBlock = (i, f, v) => setForm((p) => ({ ...p, blocks: p.blocks.map((b, idx) => idx === i ? { ...b, [f]: v } : b) }));
  const addBlock = (type) => setForm((p) => ({ ...p, blocks: [...p.blocks, { type, content: "", language: "javascript", caption: "" }] }));
  const delBlock = (i) => setForm((p) => ({ ...p, blocks: p.blocks.filter((_, idx) => idx !== i) }));
  const moveBlock = (i, dir) => { const j = i + dir; if (j < 0 || j >= form.blocks.length) return; setForm((p) => { const b = [...p.blocks]; [b[i], b[j]] = [b[j], b[i]]; return { ...p, blocks: b }; }); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "16px 20px", background: "#0E1116", border: "1px solid rgba(199,249,75,0.18)", borderRadius: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Ic name="blogs" size={18} color="rgba(199,249,75,0.7)" />
          <span style={{ fontSize: 15, fontWeight: 600, color: "rgba(237,239,242,0.7)" }}>{isEdit ? "Edit Blog" : "New Blog"}</span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <StatusToggle value={form.status || "published"} onChange={(v) => upd("status", v)} />
          <button className="adm-icon-btn" onClick={onCancel}><Ic name="x" size={13} /> Cancel</button>
          <button className="adm-btn-primary" onClick={onSave} disabled={saving || !form.title.trim()} style={{ fontSize: 13, padding: "8px 20px" }}>
            {saving ? "Saving…" : isEdit ? "Update" : "Publish"}
          </button>
        </div>
      </div>

      <Card>
        <CardHeader title="Blog Details" />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input className="adm-input" placeholder="Blog title *" value={form.title} onChange={(e) => upd("title", e.target.value)} style={{ fontSize: 17, fontWeight: 600 }} />
          <textarea className="adm-input" rows={2} placeholder="Excerpt (short description)" value={form.excerpt} onChange={(e) => upd("excerpt", e.target.value)} />
          <div>
            <FieldLabel>Thumbnail</FieldLabel>
            <MediaUpload value={form.thumbnail} onChange={(v) => upd("thumbnail", v)} label="Upload Thumbnail" maxPx={900} />
          </div>
          <div>
            <FieldLabel>Reel URL</FieldLabel>
            <input className="adm-input" placeholder="https://instagram.com/reel/…" value={form.reelUrl} onChange={(e) => upd("reelUrl", e.target.value)} />
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Content Blocks" />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {form.blocks.map((block, i) => (
            <BlockRow key={i} block={block} index={i} total={form.blocks.length}
              onUpdate={(f, v) => updBlock(i, f, v)} onDelete={() => delBlock(i)} onMove={(dir) => moveBlock(i, dir)} />
          ))}
        </div>
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <FieldLabel>Add block</FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {BLOCK_TYPES.map((t) => (
              <button key={t.id} onClick={() => addBlock(t.id)}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(237,239,242,0.45)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.17s" }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = "rgba(199,249,75,0.4)"; e.currentTarget.style.color = "#C7F94B"; e.currentTarget.style.background = "rgba(199,249,75,0.06)"; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(237,239,242,0.45)"; e.currentTarget.style.background = "transparent"; }}>
                {t.icon ? <Ic name={t.icon} size={12} /> : null}
                {t.shortLabel ? <span style={{ fontFamily: "monospace", fontSize: 11 }}>{t.shortLabel}</span> : null}
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function BlockRow({ block, index, total, onUpdate, onDelete, onMove }) {
  const bt = BLOCK_TYPES.find((t) => t.id === block.type);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.07)" }}>
      <div style={{ flexShrink: 0, width: 28, paddingTop: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {bt?.icon ? <Ic name={bt.icon} size={14} color="rgba(199,249,75,0.65)" /> : <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(199,249,75,0.65)", fontFamily: "monospace" }}>{bt?.shortLabel}</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 7 }}>
        {block.type === "divider"
          ? <div style={{ height: 1, background: "rgba(255,255,255,0.12)", borderRadius: 1, margin: "10px 0" }} />
          : block.type === "code"
            ? (<><input className="adm-input" placeholder="Language (e.g. javascript)" value={block.language || ""} onChange={(e) => onUpdate("language", e.target.value)} style={{ fontSize: 12 }} /><textarea className="adm-input" rows={5} placeholder="Code…" value={block.content} onChange={(e) => onUpdate("content", e.target.value)} style={{ fontFamily: "'Fira Code','Courier New',monospace", fontSize: 12.5 }} /></>)
          : block.type === "image"
            ? (<><MediaUpload value={block.content} onChange={(v) => onUpdate("content", v)} label="Upload Image" maxPx={900} /><input className="adm-input" placeholder="Caption (optional)" value={block.caption || ""} onChange={(e) => onUpdate("caption", e.target.value)} style={{ fontSize: 12 }} /></>)
          : block.type === "video"
            ? <MediaUpload value={block.content} onChange={(v) => onUpdate("content", v)} accept="video/*" label="Upload Video" />
          : block.type === "h2" || block.type === "h3"
            ? <input className="adm-input" placeholder={block.type === "h2" ? "Heading 2" : "Heading 3"} value={block.content} onChange={(e) => onUpdate("content", e.target.value)} style={{ fontSize: block.type === "h2" ? 18 : 15, fontWeight: 700 }} />
          : block.type === "list"
            ? <textarea className="adm-input" rows={4} placeholder={"Item 1\nItem 2\nItem 3"} value={block.content} onChange={(e) => onUpdate("content", e.target.value)} />
          : <textarea className="adm-input" rows={block.type === "quote" ? 3 : 4} placeholder={block.type === "quote" ? "Quote text…" : "Paragraph text…"} value={block.content} onChange={(e) => onUpdate("content", e.target.value)} />
        }
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
        <button onClick={() => onMove(-1)} disabled={index === 0} style={{ ...ctrlBtn, opacity: index === 0 ? 0.2 : 1 }}><Ic name="chevup" size={12} /></button>
        <button onClick={() => onMove(1)} disabled={index === total - 1} style={{ ...ctrlBtn, opacity: index === total - 1 ? 0.2 : 1 }}><Ic name="chevdown" size={12} /></button>
        <button onClick={onDelete} style={{ ...ctrlBtn, color: "rgba(239,68,68,0.55)", borderColor: "rgba(239,68,68,0.18)" }}><Ic name="x" size={12} /></button>
      </div>
    </div>
  );
}

const ctrlBtn = { background: "none", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(237,239,242,0.4)", width: 26, height: 26, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, transition: "all 0.15s" };

function SubscribersTab() {
  const [communities, setCommunities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const q = query(collection(db, "communities"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCommunities(list);
      if (!selected && list.length > 0) setSelected(list[0].id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return;
    const q = query(collection(db, "communities", selected, "subscribers"), orderBy("joinedAt", "desc"));
    return onSnapshot(q, (snap) => setSubscribers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [selected]);

  const selectedComm = communities.find((c) => c.id === selected);
  const filteredSubs = search
    ? subscribers.filter(s => [(s.name || ""), (s.email || "")].some(f => f.toLowerCase().includes(search.toLowerCase())))
    : subscribers;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {communities.map((c) => (
          <button key={c.id} onClick={() => { setSelected(c.id); setSearch(""); }}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 500, transition: "all 0.17s", border: `1px solid ${selected === c.id ? "rgba(199,249,75,0.45)" : "rgba(255,255,255,0.1)"}`, background: selected === c.id ? "rgba(199,249,75,0.08)" : "transparent", color: selected === c.id ? "#C7F94B" : "rgba(237,239,242,0.5)" }}>
            <Ic name="signal" size={13} color={selected === c.id ? "#C7F94B" : "rgba(237,239,242,0.3)"} />
            {c.name}
          </button>
        ))}
      </div>

      <div style={{ background: "#0E1116", border: "1px solid rgba(199,249,75,0.18)", borderRadius: 14, padding: "18px 22px", display: "flex", alignItems: "center", gap: 18 }}>
        <div style={{ width: 48, height: 48, borderRadius: 13, background: "rgba(199,249,75,0.08)", border: "1.5px solid rgba(199,249,75,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Ic name="subscribers" size={20} color="#C7F94B" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#EDEFF2", letterSpacing: "-0.03em", lineHeight: 1 }}>
            {search ? <>{filteredSubs.length} <span style={{ fontSize: 14, color: "rgba(237,239,242,0.35)", fontWeight: 400 }}>of {subscribers.length}</span></> : subscribers.length}
          </div>
          <div style={{ fontSize: 12, color: "rgba(237,239,242,0.4)", marginTop: 4 }}>Members · {selectedComm?.name || "—"}</div>
        </div>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name or email…" />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {subscribers.length === 0 && <EmptyState icon="subscribers" text="No members yet." />}
        {subscribers.length > 0 && filteredSubs.length === 0 && <div className="adm-no-results">No members match "{search}"</div>}
        {filteredSubs.map((s, i) => (
          <div key={s.id} style={{ background: "rgba(14,17,22,0.7)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "11px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(199,249,75,0.08)", border: "1px solid rgba(199,249,75,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "rgba(199,249,75,0.75)", flexShrink: 0 }}>{i + 1}</div>
              <div style={{ minWidth: 0 }}>
                {s.name && <div style={{ fontSize: 13, fontWeight: 600, color: "#EDEFF2", marginBottom: 2 }}>{s.name}</div>}
                <div style={{ fontSize: 12, color: "rgba(237,239,242,0.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.email}</div>
              </div>
            </div>
            <span style={{ fontSize: 11, color: "rgba(237,239,242,0.3)", flexShrink: 0 }}>{s.joinedAt?.toDate?.()?.toLocaleDateString() || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VenturesTab() {
  const [ventures, setVentures] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => {
    const q = query(collection(db, "venture_submissions"), orderBy("submittedAt", "desc"));
    return onSnapshot(q, (snap) => setVentures(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const filteredVentures = search
    ? ventures.filter(v => [v.from_name, v.startup_name, v.from_email, v.one_liner, v.stage].some(f => (f || "").toLowerCase().includes(search.toLowerCase())))
    : ventures;

  return (
    <div>
      <div className="adm-section-header" style={{ marginBottom: 12 }}>
        <span className="adm-section-count">
          {search ? `${filteredVentures.length} of ${ventures.length}` : `${ventures.length}`} submission{ventures.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="adm-filter-row" style={{ marginBottom: 14 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name, startup, email or stage…" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {ventures.length === 0 && <EmptyState icon="ventures" text="No submissions yet." />}
        {ventures.length > 0 && filteredVentures.length === 0 && <div className="adm-no-results">No submissions match "{search}"</div>}
        {filteredVentures.map((v) => (
          <div key={v.id} style={{ background: "rgba(14,17,22,0.7)", border: `1px solid ${expanded === v.id ? "rgba(199,249,75,0.25)" : "rgba(255,255,255,0.07)"}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s" }}>
            <button onClick={() => setExpanded(expanded === v.id ? null : v.id)}
              style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(199,249,75,0.08)", border: "1px solid rgba(199,249,75,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Ic name="ventures" size={16} color="rgba(199,249,75,0.7)" />
                </div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "#EDEFF2" }}>{v.from_name}</div>
                  <div style={{ fontSize: 11.5, color: "rgba(237,239,242,0.45)" }}>{v.startup_name} · {v.stage}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "rgba(237,239,242,0.3)" }}>{v.submittedAt?.toDate?.()?.toLocaleDateString() || "—"}</span>
                <Ic name={expanded === v.id ? "chevup" : "chevdown"} size={14} color="rgba(237,239,242,0.35)" />
              </div>
            </button>
            {expanded === v.id && (
              <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="adm-ventures-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px", paddingTop: 14 }}>
                  <VField label="Email" value={v.from_email} /><VField label="Phone" value={v.phone || "—"} />
                  <VField label="LinkedIn" value={v.linkedin || "—"} /><VField label="Funding" value={v.funding} />
                  <VField label="One-liner" value={v.one_liner} style={{ gridColumn: "1 / -1" }} />
                  {v.problem && <VField label="Problem" value={v.problem} style={{ gridColumn: "1 / -1" }} />}
                  <VField label="Why back?" value={v.why_back} style={{ gridColumn: "1 / -1" }} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardTab({ stats }) {
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "develop_enquiries"), orderBy("submittedAt", "desc"));
    return onSnapshot(q, (snap) => setEnquiries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const now = new Date();
  const last7  = enquiries.filter(e => { const d = e.submittedAt?.toDate?.(); return d && (now - d) < 7  * 86400000; }).length;
  const last30 = enquiries.filter(e => { const d = e.submittedAt?.toDate?.(); return d && (now - d) < 30 * 86400000; }).length;

  const countryCounts = {};
  enquiries.forEach(e => { if (e.country) countryCounts[e.country] = (countryCounts[e.country] || 0) + 1; });
  const topCountries = Object.entries(countryCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const recent = enquiries.slice(0, 6);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Enquiries hero */}
      <div style={{ background: "linear-gradient(135deg, rgba(56,189,248,0.1), rgba(56,189,248,0.04))", border: "1px solid rgba(56,189,248,0.22)", borderRadius: 18, padding: "24px 28px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: "rgba(56,189,248,0.7)", textTransform: "uppercase", marginBottom: 18 }}>Develop Enquiries</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0 }}>
          {[
            { val: enquiries.length, label: "Total" },
            { val: last30,           label: "Last 30 days" },
            { val: last7,            label: "Last 7 days" },
          ].map((s, i) => (
            <div key={i} style={{ borderRight: i < 2 ? "1px solid rgba(56,189,248,0.15)" : "none", paddingRight: i < 2 ? 24 : 0, paddingLeft: i > 0 ? 24 : 0 }}>
              <div style={{ fontSize: 46, fontWeight: 700, color: i === 0 ? "#38bdf8" : "#EDEFF2", letterSpacing: "-0.04em", lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 12, color: "rgba(237,239,242,0.4)", marginTop: 6 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent + Countries */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Recent enquiries */}
        <div style={{ background: "#0E1116", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "20px 22px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(237,239,242,0.35)", textTransform: "uppercase", marginBottom: 16 }}>Recent</div>
          {recent.length === 0 && <div style={{ fontSize: 13, color: "rgba(237,239,242,0.25)", textAlign: "center", padding: "24px 0" }}>No enquiries yet</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {recent.map(e => (
              <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#EDEFF2", marginBottom: 2 }}>{e.name}</div>
                  <div style={{ fontSize: 11.5, color: "rgba(237,239,242,0.4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.business}{e.country ? ` · ${e.country}` : ""}</div>
                  {e.contact && (
                    <div style={{ fontSize: 11, color: "rgba(56,189,248,0.65)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.contact}</div>
                  )}
                </div>
                <div style={{ fontSize: 10.5, color: "rgba(237,239,242,0.25)", flexShrink: 0, textAlign: "right", paddingTop: 2 }}>
                  {e.submittedAt?.toDate?.()?.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) || "—"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top countries */}
        <div style={{ background: "#0E1116", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "20px 22px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(237,239,242,0.35)", textTransform: "uppercase", marginBottom: 16 }}>Top Countries</div>
          {topCountries.length === 0 && <div style={{ fontSize: 13, color: "rgba(237,239,248,0.25)", textAlign: "center", padding: "24px 0" }}>No data yet</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {topCountries.map(([country, count]) => (
              <div key={country}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <span style={{ fontSize: 13, color: "rgba(237,239,242,0.75)" }}>{country}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#38bdf8" }}>{count}</span>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 2, background: "rgba(56,189,248,0.6)", width: `${Math.round(count / enquiries.length * 100)}%`, transition: "width 0.4s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Platform stats */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(237,239,242,0.35)", textTransform: "uppercase", marginBottom: 14 }}>Platform Stats</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          {[
            { label: "Communities", value: stats.communities, icon: "signal",      color: "#C7F94B" },
            { label: "Posts Live",  value: stats.posts,       icon: "posts",       color: "#818cf8" },
            { label: "Blogs Live",  value: stats.blogs,       icon: "blogs",       color: "#4ade80" },
            { label: "Ventures",    value: stats.ventures,    icon: "ventures",    color: "#fb923c" },
          ].map(s => (
            <div key={s.label} style={{ background: "#0E1116", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Ic name={s.icon} size={14} color={s.color} />
                <span style={{ fontSize: 11, color: "rgba(237,239,242,0.4)", fontWeight: 600 }}>{s.label}</span>
              </div>
              <div style={{ fontSize: 34, fontWeight: 700, color: s.color, letterSpacing: "-0.03em", lineHeight: 1 }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

function EnquiriesTab() {
  const [enquiries, setEnquiries] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const q = query(collection(db, "develop_enquiries"), orderBy("submittedAt", "desc"));
    return onSnapshot(q, (snap) => setEnquiries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const filtered = search
    ? enquiries.filter(e => [e.name, e.business, e.contact, e.whatsapp, e.country, e.what]
        .some(f => (f || "").toLowerCase().includes(search.toLowerCase())))
    : enquiries;

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await deleteDoc(doc(db, "develop_enquiries", id));
  };

  return (
    <div>
      <div className="adm-section-header" style={{ marginBottom: 12 }}>
        <span className="adm-section-count">
          {search ? `${filtered.length} of ${enquiries.length}` : `${enquiries.length}`} enquir{enquiries.length !== 1 ? "ies" : "y"}
        </span>
      </div>
      <div className="adm-filter-row" style={{ marginBottom: 14 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name, business, contact or country…" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {enquiries.length === 0 && <EmptyState icon="enquiries" text="No enquiries yet." />}
        {enquiries.length > 0 && filtered.length === 0 && <div className="adm-no-results">No enquiries match "{search}"</div>}
        {filtered.map((e) => (
          <div key={e.id} style={{ background: "rgba(14,17,22,0.7)", border: `1px solid ${expanded === e.id ? "rgba(56,189,248,0.25)" : "rgba(255,255,255,0.07)"}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s" }}>
            <button onClick={() => setExpanded(expanded === e.id ? null : e.id)}
              style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Ic name="enquiries" size={16} color="rgba(56,189,248,0.75)" />
                </div>
                <div style={{ textAlign: "left", minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "#EDEFF2" }}>{e.name}</div>
                  <div style={{ fontSize: 11.5, color: "rgba(237,239,242,0.45)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 340 }}>
                    {e.business}{e.country ? ` · ${e.country}` : ""}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: "rgba(237,239,242,0.3)" }}>{e.submittedAt?.toDate?.()?.toLocaleDateString() || "—"}</span>
                <Ic name={expanded === e.id ? "chevup" : "chevdown"} size={14} color="rgba(237,239,242,0.35)" />
              </div>
            </button>
            {expanded === e.id && (
              <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="adm-ventures-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px", paddingTop: 14 }}>
                  <VField label="Contact" value={e.contact} />
                  <VField label="WhatsApp" value={e.whatsapp || "—"} />
                  <VField label="Country" value={e.country || "—"} />
                  <VField label="Submitted" value={e.submittedAt?.toDate?.()?.toLocaleString() || "—"} />
                  {e.what && <VField label="What they need" value={e.what} style={{ gridColumn: "1 / -1" }} />}
                </div>
                <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
                  <button className="adm-icon-btn danger" onClick={() => handleDelete(e.id)}><Ic name="trash" size={13} /> Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
function VField({ label, value, style }) {
  return (
    <div style={style}>
      <div style={{ fontSize: 10, color: "rgba(237,239,242,0.35)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: "rgba(237,239,242,0.7)", lineHeight: 1.55 }}>{value}</div>
    </div>
  );
}

function resizeToBase64(file, maxPx = 400, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxPx || h > maxPx) {
          if (w >= h) { h = Math.round(h * maxPx / w); w = maxPx; }
          else { w = Math.round(w * maxPx / h); h = maxPx; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function MediaUpload({ value, onChange, accept = "image/*", label = "Upload", maxPx = 400 }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = React.useRef();
  const isVideo = accept.startsWith("video");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Select an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { setError("Max 8 MB."); return; }
    setError("");
    setBusy(true);
    try {
      const base64 = await resizeToBase64(file, maxPx, 0.82);
      onChange(base64);
    } catch {
      setError("Failed to process image.");
    } finally {
      setBusy(false);
    }
  };

  const isBase64 = value?.startsWith("data:");

  // Videos: upload not supported, URL-only
  if (isVideo) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input className="adm-input" placeholder="Paste video URL or YouTube link" value={value || ""}
            onChange={(e) => onChange(e.target.value)} style={{ fontSize: 12, padding: "8px 12px" }} />
          {value && (
            <button type="button" onClick={() => onChange("")} className="adm-icon-btn danger" style={{ flexShrink: 0, padding: "6px 8px" }}>
              <Ic name="x" size={13} />
            </button>
          )}
        </div>
        {value && <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#4ade80" }}><Ic name="check" size={12} color="#4ade80" /> URL set</div>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 9, fontSize: 12, fontWeight: 500, cursor: busy ? "not-allowed" : "pointer", fontFamily: "inherit", border: "1px solid rgba(199,249,75,0.3)", background: "rgba(199,249,75,0.06)", color: "#C7F94B", transition: "all 0.17s", opacity: busy ? 0.6 : 1, flexShrink: 0, whiteSpace: "nowrap" }}>
          <Ic name="upload" size={13} color="#C7F94B" />
          {busy ? "Processing…" : label}
        </button>
        {!isBase64 && (
          <>
            <span style={{ fontSize: 11, color: "rgba(237,239,242,0.3)" }}>or</span>
            <input className="adm-input" placeholder="Paste URL" value={value || ""} onChange={(e) => onChange(e.target.value)}
              style={{ flex: 1, fontSize: 12, padding: "8px 12px" }} />
          </>
        )}
        {value && (
          <button type="button" onClick={() => onChange("")} className="adm-icon-btn danger" style={{ flexShrink: 0, padding: "6px 8px" }}>
            <Ic name="x" size={13} />
          </button>
        )}
      </div>
      {value && !busy && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#4ade80" }}>
          <Ic name="check" size={12} color="#4ade80" /> {isBase64 ? "Image ready" : "URL set"}
        </div>
      )}
      {error && <p style={{ margin: 0, fontSize: 11.5, color: "#f87171" }}>{error}</p>}
      <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }} onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}

function Card({ children, accent }) {
  return (
    <div style={{ background: "#0E1116", border: `1px solid ${accent ? "rgba(199,249,75,0.25)" : "rgba(255,255,255,0.07)"}`, borderRadius: 16, padding: "22px 24px" }}>
      {children}
    </div>
  );
}

function CardHeader({ title, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(237,239,242,0.45)", letterSpacing: "0.06em", textTransform: "uppercase" }}>{title}</span>
      {children && <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{children}</div>}
    </div>
  );
}

function FieldLabel({ children }) {
  return <div style={{ fontSize: 11, color: "rgba(237,239,242,0.35)", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 7 }}>{children}</div>;
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: "center", padding: "52px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Ic name={icon} size={20} color="rgba(237,239,242,0.2)" />
      </div>
      <p style={{ margin: 0, fontSize: 13, color: "rgba(237,239,242,0.28)" }}>{text}</p>
    </div>
  );
}

function Shell({ children, align = "center" }) {
  return (
    <div style={{ minHeight: "100vh", width: "100%", position: "relative", overflow: "hidden", background: "#0A0B0D", fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif", color: "#EDEFF2", display: "flex", flexDirection: "column", alignItems: align === "center" ? "center" : "stretch", justifyContent: align === "center" ? "center" : "flex-start" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: "-20%", left: "-5%", width: "55%", height: "55%", borderRadius: "50%", background: "radial-gradient(circle, rgba(199,249,75,0.04) 0%, transparent 65%)" }} />
        <div style={{ position: "absolute", bottom: "0%", right: "0%", width: "40%", height: "40%", borderRadius: "50%", background: "radial-gradient(circle, rgba(199,249,75,0.06) 0%, transparent 65%)" }} />
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "64px 64px" }} />
      </div>
      <style>{`
        .adm-input { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); color:#EDEFF2; outline:none; width:100%; border-radius:10px; padding:11px 14px; font-size:13.5px; resize:vertical; box-sizing:border-box; font-family:inherit; transition:border-color .2s,box-shadow .2s; }
        .adm-input::placeholder { color:rgba(237,239,242,0.3); }
        .adm-input:focus { border-color:rgba(199,249,75,0.45); box-shadow:0 0 0 3px rgba(199,249,75,0.07); }
        .adm-btn-primary { background:linear-gradient(135deg,#8aae30,#C7F94B); color:#0A0B0D; font-weight:700; border:none; border-radius:10px; padding:11px 24px; font-size:13.5px; cursor:pointer; font-family:inherit; transition:box-shadow .2s,transform .15s,opacity .15s; display:inline-flex; align-items:center; gap:7px; }
        .adm-btn-primary:hover:not(:disabled) { box-shadow:0 4px 24px rgba(199,249,75,0.3); transform:translateY(-1px); }
        .adm-btn-primary:disabled { opacity:.5; cursor:not-allowed; }
        @media (max-width:900px) {
          .adm-compose-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width:700px) {
          .adm-stat-grid { grid-template-columns: repeat(2,1fr) !important; }
          .adm-header { flex-wrap: wrap; gap: 12px !important; }
          .adm-ventures-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width:440px) {
          .adm-stat-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
      <div style={{ position: "relative", zIndex: 1, width: "100%", display: "flex", flexDirection: "column", alignItems: align === "center" ? "center" : "stretch" }}>{children}</div>
    </div>
  );
}

function Loader() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0A0B0D", fontFamily: "'Inter',sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(237,239,242,0.3)", fontSize: 13 }}>
        <Ic name="signal" size={16} color="rgba(199,249,75,0.5)" />
        Loading…
      </div>
    </div>
  );
}
